import { apiRequest } from './api';
import { getApiUrl, getApiUrlWithParams } from '../config/api';

export const financialApi = {
  // Phase 1: Dashboard & Financial Control
  getDashboard: async () => {
    // Expected to return kpis, urgent_actions, recent_transactions
    const url = getApiUrl('financial/dashboard');
    return apiRequest(url, { method: 'GET' });
  },

  getProfitAndLoss: async (startDate, endDate) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    const url = Object.keys(params).length > 0 
      ? getApiUrlWithParams('financial/profit-and-loss', params)
      : getApiUrl('financial/profit-and-loss');
      
    return apiRequest(url, { method: 'GET' });
  },

  getBudgetVsActual: async (year, month) => {
    const params = {};
    if (year) params.period_year = year;
    if (month) params.period_month = month;
    
    const url = Object.keys(params).length > 0
      ? getApiUrlWithParams('financial/budgets/vs-actual', params)
      : getApiUrl('financial/budgets/vs-actual');
      
    return apiRequest(url, { method: 'GET' });
  },

  getCashflowForecast: async () => {
    const url = getApiUrl('financial/cashflow-forecast');
    return apiRequest(url, { method: 'GET' });
  },

  // Phase 2: Approvals & Transaction Center
  getPendingApprovals: async () => {
    const url = getApiUrl('financial/approvals/pending');
    return apiRequest(url, { method: 'GET' });
  },

  decidePayment: async (paymentId, decisionData) => {
    const url = getApiUrl(`financial/approvals/${paymentId}/decide`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(decisionData),
    });
  },

  registerDealPayment: async (dealId, formData) => {
    const url = getApiUrl(`financial/deals/${dealId}/payments`);
    // formData is expected to be a FormData object since it contains a file
    return apiRequest(url, {
      method: 'POST',
      body: formData,
      // Omit Content-Type to let the browser set it with the correct boundary for multipart/form-data
      headers: {
        'Accept': 'application/json',
      }
    });
  },

  matchWebOrder: async (orderData) => {
    const url = getApiUrl('financial/match-web-order');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  },

  getTransactions: async (params) => {
    const url = Object.keys(params || {}).length > 0
      ? getApiUrlWithParams('financial/transactions', params)
      : getApiUrl('financial/transactions');
    return apiRequest(url, { method: 'GET' });
  },

  getAuditLogs: async (params) => {
    const url = Object.keys(params || {}).length > 0
      ? getApiUrlWithParams('financial/audit-logs', params)
      : getApiUrl('financial/audit-logs');
    return apiRequest(url, { method: 'GET' });
  },

  // Phase 3: Receivables, Installments & Cheques
  generateInstallments: async (dealId, data) => {
    const url = getApiUrl(`financial/deals/${dealId}/installments`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  registerCheque: async (data) => {
    const url = getApiUrl('financial/cheques');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  changeChequeStatus: async (id, data) => {
    const url = getApiUrl(`financial/cheques/${id}/status`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getReceivablesSummary: async () => {
    const url = getApiUrl('financial/receivables/summary');
    return apiRequest(url, { method: 'GET' });
  },

  getAgingReport: async () => {
    const url = getApiUrl('financial/receivables/aging');
    return apiRequest(url, { method: 'GET' });
  },

  // Phase 6: Refunds, Discounts & Client 360
  getRefunds: async (params) => {
    const url = Object.keys(params || {}).length > 0 
      ? getApiUrlWithParams('financial/refunds', params)
      : getApiUrl('financial/refunds');
    return apiRequest(url, { method: 'GET' });
  },

  createRefund: async (data) => {
    const url = getApiUrl('financial/refunds');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  approveRefund: async (id) => {
    const url = getApiUrl(`financial/refunds/${id}/approve`);
    return apiRequest(url, { method: 'POST' });
  },

  executeRefund: async (id, data) => {
    const url = getApiUrl(`financial/refunds/${id}/execute`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  setDiscountLimit: async (data) => {
    const url = getApiUrl('financial/discount-limits');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  validateDiscount: async (data) => {
    const url = getApiUrl('financial/validate-discount');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getClientFinancialProfile: async (clientId) => {
    const url = getApiUrl(`financial/clients/${clientId}/financial-profile`);
    return apiRequest(url, { method: 'GET' });
  },
};
